import React, { useState, useRef, useEffect } from "react";
import { API, useLanguage } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Send,
  RotateCcw,
  Bot,
  User,
  Zap,
  Activity,
  Hash,
  ChevronRight,
  Loader2,
  Smartphone,
  CalendarCheck
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const STATE_LABELS = {
  IDLE: { label: "In attesa", color: "bg-slate-400" },
  GREETING: { label: "Saluto", color: "bg-blue-500" },
  SERVICE_SELECTION: { label: "Scelta servizio", color: "bg-violet-500" },
  DATE_SELECTION: { label: "Scelta data", color: "bg-indigo-500" },
  TIME_SELECTION: { label: "Scelta orario", color: "bg-cyan-500" },
  NAME_INPUT: { label: "Inserimento nome", color: "bg-amber-500" },
  CONFIRMATION: { label: "Conferma", color: "bg-orange-500" },
  BOOKING_COMPLETE: { label: "Prenotato!", color: "bg-emerald-500" },
  INFO_REQUEST: { label: "Info", color: "bg-teal-500" },
  PRICING_REQUEST: { label: "Prezzi", color: "bg-pink-500" },
};

const INTENT_LABELS = {
  booking: { label: "Prenotazione", color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400" },
  pricing: { label: "Prezzi", color: "text-pink-600 bg-pink-50 dark:bg-pink-900/30 dark:text-pink-400" },
  info: { label: "Informazioni", color: "text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400" },
  services: { label: "Servizi", color: "text-violet-600 bg-violet-50 dark:bg-violet-900/30 dark:text-violet-400" },
  confirm: { label: "Conferma", color: "text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400" },
  cancel: { label: "Annulla", color: "text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-400" },
  selection: { label: "Selezione", color: "text-indigo-600 bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-400" },
  generic: { label: "Generico", color: "text-slate-600 bg-slate-50 dark:bg-slate-800 dark:text-slate-400" },
};

const WhatsAppSimulator = () => {
  const { language } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `sim_${Date.now()}`);
  const [currentState, setCurrentState] = useState("IDLE");
  const [currentIntent, setCurrentIntent] = useState(null);
  const [context, setContext] = useState({});
  const [conversationId, setConversationId] = useState(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setMessages(prev => [...prev, { role: "customer", text, timestamp: new Date().toISOString() }]);
    setInput("");
    setSending(true);

    try {
      const res = await axios.post(`${API}/wa/simulator/send`, {
        message: text,
        session_id: sessionId
      }, { withCredentials: true });

      const d = res.data;
      setMessages(prev => [...prev, { role: "system", text: d.response, timestamp: new Date().toISOString() }]);
      setCurrentState(d.state || "IDLE");
      setCurrentIntent(d.intent);
      setContext(d.context || {});
      setConversationId(d.conversation_id);
    } catch (err) {
      toast.error("Errore simulatore");
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const resetConversation = async () => {
    try {
      await axios.post(`${API}/wa/simulator/reset`, {
        message: "",
        session_id: sessionId
      }, { withCredentials: true });
      setMessages([]);
      setCurrentState("IDLE");
      setCurrentIntent(null);
      setContext({});
      setConversationId(null);
      toast.success(language === "it" ? "Conversazione resettata" : "Conversation reset");
    } catch {
      toast.error("Errore reset");
    }
  };

  const quickSend = (text) => {
    setInput(text);
    setTimeout(() => {
      const fakeEvent = { preventDefault: () => {} };
      setInput(text);
    }, 0);
  };

  const stateInfo = STATE_LABELS[currentState] || { label: currentState, color: "bg-slate-400" };
  const intentInfo = currentIntent ? (INTENT_LABELS[currentIntent] || { label: currentIntent, color: "text-slate-600 bg-slate-50" }) : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[600px]" data-testid="wa-simulator">
      {/* Chat Interface */}
      <div className="lg:col-span-2 flex flex-col rounded-2xl border border-border overflow-hidden bg-card">
        {/* Chat Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-emerald-600 dark:bg-emerald-700 text-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
              <Smartphone className="w-5 h-5" />
            </div>
            <div>
              <p className="font-semibold text-sm">Bot Simulator</p>
              <p className="text-[11px] text-emerald-100">
                {language === "it" ? "Ambiente isolato — nessun messaggio reale" : "Isolated environment — no real messages"}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={resetConversation}
            className="text-white hover:bg-white/20 rounded-full h-8 px-3"
            data-testid="sim-reset-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            Reset
          </Button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#e5ddd5] dark:bg-slate-900/50" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}>
          {messages.length === 0 && (
            <div className="text-center py-12">
              <Bot className="w-12 h-12 mx-auto mb-3 text-slate-400 opacity-40" />
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {language === "it" ? "Scrivi un messaggio per iniziare la simulazione" : "Type a message to start the simulation"}
              </p>
              <div className="flex flex-wrap justify-center gap-2 mt-4">
                {["Ciao", "Vorrei prenotare", "Prezzi", "Informazioni"].map(q => (
                  <button
                    key={q}
                    onClick={() => { setInput(q); setTimeout(() => inputRef.current?.focus(), 50); }}
                    className="px-3 py-1.5 rounded-full bg-white dark:bg-slate-800 text-sm text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    data-testid={`sim-quick-${q.toLowerCase().replace(/ /g, '-')}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === "customer" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-xl px-3.5 py-2 shadow-sm ${
                msg.role === "customer"
                  ? "bg-emerald-100 dark:bg-emerald-800/60 text-slate-900 dark:text-white rounded-tr-sm"
                  : "bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-tl-sm"
              }`}>
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.text}</p>
                <p className={`text-[10px] mt-1 text-right ${msg.role === "customer" ? "text-emerald-600/60 dark:text-emerald-300/50" : "text-slate-400"}`}>
                  {new Date(msg.timestamp).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
            </div>
          ))}

          {sending && (
            <div className="flex justify-start">
              <div className="bg-white dark:bg-slate-800 rounded-xl px-4 py-2.5 shadow-sm rounded-tl-sm">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <form
          onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
          className="flex items-center gap-2 p-3 border-t border-border bg-card"
        >
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={language === "it" ? "Scrivi un messaggio..." : "Type a message..."}
            className="flex-1 rounded-full h-10 bg-muted border-0 focus-visible:ring-1"
            disabled={sending}
            data-testid="sim-input"
          />
          <Button
            type="submit"
            size="icon"
            disabled={!input.trim() || sending}
            className="rounded-full w-10 h-10 bg-emerald-500 hover:bg-emerald-400 text-white flex-shrink-0"
            data-testid="sim-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </form>
      </div>

      {/* Debug Panel */}
      <div className="flex flex-col gap-3" data-testid="sim-debug-panel">
        {/* State */}
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5" />
            {language === "it" ? "Stato Conversazione" : "Conversation State"}
          </p>
          <div className="flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${stateInfo.color}`} />
            <span className="font-semibold text-foreground text-sm">{stateInfo.label}</span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 font-mono">{currentState}</p>
        </div>

        {/* Intent */}
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" />
            {language === "it" ? "Intent Rilevato" : "Detected Intent"}
          </p>
          {intentInfo ? (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${intentInfo.color}`}>
              {intentInfo.label}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </div>

        {/* Context */}
        <div className="rounded-xl border border-border bg-card p-4 flex-1 overflow-auto">
          <p className="text-xs font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
            <Hash className="w-3.5 h-3.5" />
            {language === "it" ? "Contesto Attivo" : "Active Context"}
          </p>
          {Object.keys(context).length > 0 ? (
            <div className="space-y-1.5">
              {Object.entries(context).map(([k, v]) => v && (
                <div key={k} className="flex items-start gap-2">
                  <span className="text-[11px] text-muted-foreground font-mono min-w-[90px]">{k}:</span>
                  <span className="text-xs text-foreground font-medium">{String(v)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">{language === "it" ? "Nessun contesto" : "No context"}</p>
          )}
        </div>

        {/* Flow Steps */}
        <div className="rounded-xl border border-border bg-card p-4">
          <p className="text-xs font-medium text-muted-foreground mb-3 flex items-center gap-1.5">
            <ChevronRight className="w-3.5 h-3.5" />
            {language === "it" ? "Step del Flow" : "Flow Steps"}
          </p>
          <div className="space-y-1">
            {["GREETING", "SERVICE_SELECTION", "DATE_SELECTION", "TIME_SELECTION", "NAME_INPUT", "CONFIRMATION", "BOOKING_COMPLETE"].map((s, i) => {
              const info = STATE_LABELS[s];
              const isPast = ["GREETING", "SERVICE_SELECTION", "DATE_SELECTION", "TIME_SELECTION", "NAME_INPUT", "CONFIRMATION", "BOOKING_COMPLETE"].indexOf(currentState) > i;
              const isCurrent = currentState === s;
              return (
                <div key={s} className={`flex items-center gap-2 py-1 ${isCurrent ? "opacity-100" : isPast ? "opacity-60" : "opacity-30"}`}>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isCurrent ? info.color : isPast ? "bg-emerald-400" : "bg-slate-300 dark:bg-slate-600"}`} />
                  <span className={`text-xs ${isCurrent ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                    {info.label}
                  </span>
                  {isCurrent && <ChevronRight className="w-3 h-3 text-emerald-500 ml-auto" />}
                  {isPast && <CalendarCheck className="w-3 h-3 text-emerald-400/60 ml-auto" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppSimulator;
